import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Archive, ArrowLeft, ArrowRightLeft, Lock, ShieldAlert } from "lucide-react";

const NAVY = "#1C4358";

const COLL_LABEL = {
  leads: "Leads", referrals: "Referrals", room_documents: "Documents", tasks: "Tasks",
};

export default function LegacyUnassigned() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [sel, setSel] = useState({}); // key `${coll}:${id}` -> target branch id
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/franchise-admin/legacy-unassigned");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(errMsg(e, "Could not load Legacy / Unassigned."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reassign = async (coll, id) => {
    const target = sel[`${coll}:${id}`];
    if (!target) { toast.error("Pick a target branch first."); return; }
    setBusy(`${coll}:${id}`);
    try {
      const { data: res } = await api.post("/franchise-admin/legacy-unassigned/reassign", {
        collection: coll, record_ids: [id], target_branch_id: target, note,
      });
      if (res.reassigned > 0) toast.success(`Reassigned to ${res.target.name}.`);
      else toast.error("Record was not in Legacy (skipped).");
      await load();
    } catch (e) { toast.error(errMsg(e, "Reassignment failed.")); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  if (denied) return (
    <div className="max-w-2xl mx-auto py-24 text-center" data-testid="legacy-denied">
      <ShieldAlert size={30} className="mx-auto text-[#9C1E22]" />
      <p className="mt-3 text-[#4B5563]">Legacy / Unassigned is <b>Super Admin only</b>.</p>
      <Button variant="outline" className="mt-4 border-[#D6E0E7] text-[#1C4358]" onClick={() => navigate("/franchise-admin")}>Back to Franchise Manager</Button>
    </div>
  );

  const targets = data?.reassign_targets || [];
  const activeCollections = (data?.collections || []).filter((c) => (data.reassignable_collections || []).includes(c.collection));

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="legacy-unassigned-page">
      <Button variant="ghost" size="sm" className="text-[#1C4358] gap-1 mb-2" onClick={() => navigate("/franchise-admin")} data-testid="legacy-back">
        <ArrowLeft size={14} /> Franchise Manager
      </Button>
      <div className="flex items-center gap-2">
        <Archive size={22} style={{ color: NAVY }} />
        <h1 className="text-2xl font-display font-bold text-[#1C4358]">Legacy / Unassigned</h1>
      </div>
      <p className="text-[14px] text-[#4B5563] mt-1">
        {data?.total_records ?? 0} record(s) held here, visible to Super Admin only. Reassign records to a real branch as territories grow.
      </p>

      <Card className="mt-4 p-3.5 border-[#E6EDF2] bg-[rgba(28,67,88,0.03)] flex items-center gap-2 text-[13px] text-[#1C4358]">
        <Lock size={14} /> Reassignment is Super Admin only. Records become visible to the new branch's scoped users and leave this list.
      </Card>

      <div className="mt-4 space-y-1.5">
        <label className="text-[12px] font-semibold text-[#1C4358]">Reassignment note (optional, logged)</label>
        <Input data-testid="legacy-note" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Confirmed Kansas City address on file" className="max-w-md" />
      </div>

      {activeCollections.length === 0 && (
        <div className="text-center py-16 text-[#4B5563]" data-testid="legacy-empty">
          Nothing in Legacy / Unassigned. 🎉
        </div>
      )}

      <div className="mt-5 space-y-6">
        {activeCollections.map((c) => (
          <div key={c.collection} data-testid={`legacy-group-${c.collection}`}>
            <h2 className="text-[15px] font-display font-semibold text-[#1C4358] mb-2">
              {COLL_LABEL[c.collection] || c.collection} <span className="text-[12px] text-[#7A8A99]">({c.count})</span>
            </h2>
            <div className="space-y-2">
              {(c.samples || []).map((r) => {
                const key = `${c.collection}:${r.id}`;
                return (
                  <Card key={r.id} className="p-3 border-[#E6EDF2] flex items-center gap-3 flex-wrap" data-testid={`legacy-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#252221] text-[14px] truncate">{r.label}</div>
                      <div className="text-[12px] text-[#7A8A99]">{r.reason}</div>
                    </div>
                    <Select value={sel[key] || ""} onValueChange={(v) => setSel((s) => ({ ...s, [key]: v }))}>
                      <SelectTrigger className="h-9 w-[220px] text-xs bg-white" data-testid={`legacy-target-${r.id}`}>
                        <SelectValue placeholder="Reassign to branch…" />
                      </SelectTrigger>
                      <SelectContent>
                        {targets.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" disabled={busy === key || !sel[key]} onClick={() => reassign(c.collection, r.id)}
                      className="bg-[#1C4358] hover:bg-[#14303f] text-white gap-1.5" data-testid={`legacy-reassign-${r.id}`}>
                      {busy === key ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />} Reassign
                    </Button>
                  </Card>
                );
              })}
              {c.count > (c.samples || []).length && (
                <p className="text-[12px] text-[#7A8A99]">Showing {c.samples.length} of {c.count}. Reassign these, then reload for more.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
